import { LocalTrack, MediaDeviceFailure } from "livekit-client";
import { reactive, ref, shallowRef } from "vue";
import { fillDeviceList, initDevices } from "./devices";
import { tracksTask } from "./createTrackTask";
import { activatePipeline, currentSessionGeneration, isCurrentSession, sessionActive, stopSession } from "./pipe";
import { loadBlur, saveBlur } from "./videoBlur";
import { loadCustomBackground } from "./customBackground";
import {
  clearMicroProcessor,
  destroyWorkletNode,
  noiseSuppression,
} from "./audioProcessor";
import { cleanupLivekit } from "./livekit";
import { apiReset } from "./api";
import type { AudioProcessor } from "../../utils/AudioProcessor";
import { ws_instance } from "../../websocket/ws-instance";
import { authState } from "../auth";
import { mediaActions } from "./actions";
import { log } from "../../utils/logger";

export type BackgroundEffect =
  | { kind: 'none' }
  | { kind: 'blur'; strength: 'weak' | 'strong' }
  | { kind: 'preset'; id: string }
  | { kind: 'custom' };

export const BACKGROUND_BLUR_STRENGTHS = {
  weak: 5 / 1080,
  strong: 15 / 1080,
} as const;

export const desiredMediaState = reactive({
  // Это менять с особой осторожностью
  cameraEnabled: true,
  // Это менять с особой осторожностью
  microEnabled: true,
  /**
   * null - auto
   */
  cameraDevice: null as string | null,
  microDevice: null as string | null,
  audioDevice: null as string | null,

  /**
   * settings
   * Запрос трека камеры когда открыты настройки
   */
  cameraOnSettings: false,

  backgroundEffect: { kind: 'none' } as BackgroundEffect,
  blurSize: BACKGROUND_BLUR_STRENGTHS.strong,
  customBackground: null as string | null,
});

/**
 * Желаемое состояние, зафиксированное на время одного прохода пайплайна (или
 * init). Проход целиком работает с одним снапшотом: события, пришедшие во
 * время прохода, меняют желаемое состояние и применяются следующим проходом.
 */
export interface MediaDesired {
  cameraEnabled: boolean;
  microEnabled: boolean;
  cameraOnSettings: boolean;
  cameraDevice: string | null;
  microDevice: string | null;
  audioDevice: string | null;
  backgroundEffect: BackgroundEffect;
  blurSize: number;
  customBackground: string | null;
  noiseSuppression: boolean;
  /** Поколение сессии на момент снятия снапшота: коммиты разрешены, только
   *  пока поколение не сменилось (cleanup). */
  generation: number;
}

export function takeMediaDesired(): MediaDesired {
  return {
    cameraEnabled: desiredMediaState.cameraEnabled,
    microEnabled: desiredMediaState.microEnabled,
    cameraOnSettings: desiredMediaState.cameraOnSettings,
    cameraDevice: desiredMediaState.cameraDevice,
    microDevice: desiredMediaState.microDevice,
    audioDevice: desiredMediaState.audioDevice,
    backgroundEffect: desiredMediaState.backgroundEffect,
    blurSize: desiredMediaState.blurSize,
    customBackground: desiredMediaState.customBackground,
    noiseSuppression: noiseSuppression.value,
    generation: currentSessionGeneration(),
  };
}

// Фактическое состояние
export const microTrack = shallowRef<LocalTrack | null>(null);
export const cameraTrack = shallowRef<LocalTrack | null>(null);
export const microTrackForProcessing = shallowRef<MediaStreamTrack | null>(null);

export const microDeviceError = ref<MediaDeviceFailure | null>(null);
export const cameraDeviceError = ref<MediaDeviceFailure | null>(null);

export const realCameraDevice = ref<string | null>(null);
export const realMicroDevice = ref<string | null>(null);

/**
 * init single-flight: повторные вызовы, пока сессия активна или стартует, —
 * no-op. Сессии не сериализуются очередью: init, висящий на permission-промпте
 * старой сессии, не должен блокировать init новой. «Устаревшее» тело init
 * (пережившее cleanup) само погаснет: после каждого await оно сверяет
 * поколение сессии и не делает коммитов (задачи гейтчены isCurrentSession).
 */
export function init(): Promise<void> {
  if (sessionActive.value) return Promise.resolve();
  return startSession();
}

async function startSession() {
  if (sessionActive.value) return;
  sessionActive.value = true;
  const generation = currentSessionGeneration();
  try {
    initDevices();
    loadBlur();
    await loadCustomBackground();
    if (!isCurrentSession(generation)) return; // cleanup прошёл, пока грузился фон
    if (desiredMediaState.backgroundEffect.kind === 'custom' && !desiredMediaState.customBackground) {
      desiredMediaState.backgroundEffect = { kind: 'none' };
      saveBlur();
    }
    await tracksTask(takeMediaDesired());
    if (!isCurrentSession(generation)) return; // cleanup прошёл — задачи сами погасили треки
    await fillDeviceList();
    if (!isCurrentSession(generation)) return;
    activatePipeline();
  } catch (e) {
    // Сбой init не должен оставлять сессию «активной» навсегда: иначе каждый
    // следующий init() станет no-op, а экшены (гейт sessionActive) продолжат
    // принимать ввод, который никогда не сойдётся. stopSession() возвращает
    // машину состояний к дефолту — init() можно повторить.
    log.error("startSession failed", e);
    stopSession();
  }
}

export function cleanup() {
  // Стоп-кран: с этой точки ни один незавершённый async-колбэк не создаст
  // трек/публикацию и не изменит состояние (коммиты сверяют поколение сессии
  // через isCurrentSession после каждого await).
  stopSession();
  // Желаемое состояние следующей сессии — только дефолты, без наследия.
  desiredMediaState.cameraEnabled = true;
  desiredMediaState.microEnabled = true;
  desiredMediaState.cameraOnSettings = false;
  desiredMediaState.cameraDevice = null;
  desiredMediaState.microDevice = null;
  desiredMediaState.audioDevice = null;
  desiredMediaState.backgroundEffect = { kind: 'none' };
  desiredMediaState.blurSize = BACKGROUND_BLUR_STRENGTHS.strong;
  desiredMediaState.customBackground = null;

  if (microTrack.value) {
    const processor = microTrack.value.getProcessor() as AudioProcessor;
    if (processor) {
      // Терминальная очистка графа микрофонного контекста (VAD/шумодав).
      clearMicroProcessor(processor);
    }
    microTrack.value.stop();
    microTrack.value = null;
    realMicroDevice.value = null;
  }
  if (cameraTrack.value) {
    cameraTrack.value.stop();
    cameraTrack.value = null;
    realCameraDevice.value = null;
  }
  if (microTrackForProcessing.value) {
    microTrackForProcessing.value.stop();
    destroyWorkletNode(microTrackForProcessing.value);
    microTrackForProcessing.value = null;
  }
  microDeviceError.value = null;
  cameraDeviceError.value = null;
  cleanupLivekit();
  apiReset();
}

export const lockOnMicro = ref(false)

let allMicroOffSubscribed = false;
let muteUserSubscribed = false;

export function subscribeAllMicroOff() {
  // Подписка регистрируется один раз на всё время жизни модуля; вызовы из
  // connectToActiveRoom при каждом входе в комнату больше не плодят дубли.
  // События «мёртвой» сессии (после cleanup) игнорируются: они не должны
  // влиять на желаемое состояние следующей сессии.
  if (allMicroOffSubscribed) return;
  allMicroOffSubscribed = true;
  ws_instance.addEventListener("all-micro-off", (data: string) => {
    if (!sessionActive.value) return;
    if(data !== authState.id) {
      mediaActions.microOff()
      lockOnMicro.value = true
      setTimeout(() => {
        lockOnMicro.value = false
      }, 5000)
    }
  });
}

export function subscribeMuteUser() {
  if (muteUserSubscribed) return;
  muteUserSubscribed = true;
  ws_instance.addEventListener("mute-user", (userId: string) => {
    if (!sessionActive.value) return;
    if (userId === authState.id) {
      mediaActions.microOff()
    }
  });
}
